import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useOrgLibrary, useSetVisibility, useSetCourseType, useDeleteOrgCourse } from "@/hooks/useOrgLibrary";
import { CompliancePanel } from "@/components/CompliancePanel";
import { Button } from "@/components/ui/button";
import { Library, Play, Trash2, Zap, Coffee, Layers, Lock, Globe, Users2, User, Check, ShieldAlert, BookOpen } from "lucide-react";

const ICONS = { overview: Zap, lunch: Coffee, comprehensive: Layers };
const VIS_META = {
  draft: { label: "Draft", icon: Lock, cls: "text-muted-foreground" },
  org: { label: "Whole org", icon: Globe, cls: "text-primary" },
  teams: { label: "Teams", icon: Users2, cls: "text-primary" },
  members: { label: "Members", icon: User, cls: "text-primary" },
};
const TYPE_META = {
  mandatory: { label: "Mandatory", icon: ShieldAlert, cls: "text-amber-700 bg-amber-500/10 border border-amber-500/30" },
  elective: { label: "Elective", icon: BookOpen, cls: "text-muted-foreground bg-secondary" },
};

function VisibilityEditor({ course, teams, members }) {
  const [vis, setVis] = useState(course.visibility || "draft");
  const [teamIds, setTeamIds] = useState(course.team_ids || []);
  const [memberIds, setMemberIds] = useState(course.member_ids || []);

  const save = useSetVisibility({ onSuccess: () => toast.success("Sharing updated"), onError: (e) => toast.error(apiError(e)) });
  const setType = useSetCourseType({ onSuccess: (d) => toast.success(`Marked as ${d.course_type}`), onError: (e) => toast.error(apiError(e)) });
  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const onSave = () => {
    if (vis === "teams" && teamIds.length === 0) { toast.error("Pick at least one team"); return; }
    if (vis === "members" && memberIds.length === 0) { toast.error("Pick at least one member"); return; }
    save.mutate({ id: course.id, visibility: vis, team_ids: teamIds, member_ids: memberIds });
  };

  return (
    <div className="mt-4 border-t border-border pt-4" data-testid={`visibility-editor-${course.id}`}>
      <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-2">Course type</label>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {["elective", "mandatory"].map((t) => (
          <button key={t} onClick={() => (course.course_type || "elective") !== t && setType.mutate({ id: course.id, course_type: t })}
            className={`border p-2 text-sm capitalize ${(course.course_type || "elective") === t ? "border-primary bg-primary/5 font-semibold" : "border-border text-muted-foreground"}`}
            data-testid={`type-${t}-${course.id}`}>
            {t}
          </button>
        ))}
      </div>

      <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-2">Who can see this</label>
      <select value={vis} onChange={(e) => setVis(e.target.value)} className="border border-border h-9 text-sm px-2 bg-background w-full mb-3" data-testid={`visibility-select-${course.id}`}>
        <option value="draft">Draft — managers only</option>
        <option value="org">Whole organization</option>
        <option value="teams">Specific teams</option>
        <option value="members">Specific members</option>
      </select>

      {vis === "teams" && (
        <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
          {teams.length === 0 ? <p className="text-xs text-muted-foreground">No teams yet — create some on the Organization page.</p> :
            teams.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`vis-team-${course.id}-${t.id}`}>
                <input type="checkbox" checked={teamIds.includes(t.id)} onChange={() => toggle(teamIds, setTeamIds, t.id)} className="accent-primary" /> {t.name}
              </label>
            ))}
        </div>
      )}
      {vis === "members" && (
        <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
          {members.map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`vis-member-${course.id}-${m.id}`}>
              <input type="checkbox" checked={memberIds.includes(m.id)} onChange={() => toggle(memberIds, setMemberIds, m.id)} className="accent-primary" /> {m.name}
            </label>
          ))}
        </div>
      )}

      <Button size="sm" onClick={onSave} disabled={save.isPending} className="rounded-none bg-primary hover:opacity-90 gap-1 w-full" data-testid={`save-visibility-${course.id}`}>
        <Check className="h-4 w-4" /> {save.isPending ? "Saving..." : "Update sharing"}
      </Button>
    </div>
  );
}

export default function OrgLibrary() {
  const { data, isLoading } = useOrgLibrary();
  const courses = data?.courses || [];
  const teams = data?.teams || [];
  const members = data?.members || [];
  const isManager = data?.is_manager;

  const del = useDeleteOrgCourse({ onSuccess: () => toast.success("Course deleted"), onError: (e) => toast.error(apiError(e)) });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Library className="h-5 w-5 text-primary" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Team Library</p>
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter mb-1">Company training</h1>
        <p className="text-muted-foreground mb-10">
          {isManager ? "Courses generated from your organization's knowledge. Set who can see each one." : "Training courses shared with you by your organization."}
        </p>

        {isManager && !isLoading && <CompliancePanel />}

        {isLoading ? (
          <div className="py-20 flex justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" /></div>
        ) : courses.length === 0 ? (
          <div className="border border-dashed border-border bg-card p-16 text-center" data-testid="library-empty">
            <h3 className="font-display text-xl font-bold mb-2">Nothing here yet</h3>
            <p className="text-muted-foreground">{isManager ? "Upload documents and bulk-generate courses from the Organization page." : "Your managers haven't shared any courses with you yet."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="org-course-grid">
            {courses.map((c, i) => {
              const Icon = ICONS[c.length] || Zap;
              const vm = VIS_META[c.visibility] || VIS_META.draft;
              const VIcon = vm.icon;
              return (
                <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="border border-border bg-card p-6 flex flex-col" data-testid={`org-course-${c.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-10 w-10 bg-secondary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                    <div className="flex items-center gap-3">
                      {isManager && (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${vm.cls}`} data-testid={`vis-badge-${c.id}`}>
                          <VIcon className="h-3.5 w-3.5" /> {vm.label}
                        </span>
                      )}
                      {isManager && (
                        <button onClick={() => del.mutate(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-org-course-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                  <Link to={`/course/${c.id}`} className="group block" data-testid={`org-course-link-${c.id}`}>
                    {(() => { const tm = TYPE_META[c.course_type] || TYPE_META.elective; const TIcon = tm.icon; return (
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 mb-2 ${tm.cls}`} data-testid={`type-badge-${c.id}`}>
                        <TIcon className="h-3 w-3" /> {tm.label}
                      </span>
                    ); })()}
                    <h3 className="font-display text-lg font-bold leading-tight mb-1 line-clamp-2 group-hover:text-primary transition-colors">{c.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{c.subtitle}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="uppercase tracking-wider font-semibold text-muted-foreground">{c.length_label}</span>
                      <span className="inline-flex items-center gap-1 text-primary font-semibold"><Play className="h-3.5 w-3.5" /> Watch</span>
                    </div>
                  </Link>
                  {isManager && <VisibilityEditor course={c} teams={teams} members={members} />}
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
